<script setup lang="ts">
import DatePicker from '@/components/Common/DatePicker.vue';
import i18n from '@/lang/i18n';
import { computed } from "vue";
import UnexpectedEventFileType from '@/types/UnexpectedEventFile'
import RegisteredFile from '@/components/DailyReportUpdateModalParts/UnexpectedEventShowRegisteredFile.vue';

const emits = defineEmits(['update:modelValue'])
const props = defineProps<{
  modelValue: {
    delivery_plan_id: string,
    start_time: string,
    end_time: string,
    address: string,
    note: string,
    files: any[]
  },
  registeredFiles: UnexpectedEventFileType[],
  errors: {
    'start_time': string[],
    'end_time': string[],
    'address': string[],
    'note': string[],
    'files.0': string[]
    'files.1': string[]
    'files.2': string[]
    'files.3': string[]
    'files.4': string[]
  },
  deleteRegisteredFile: (id: number, file: UnexpectedEventFileType) => void
  downloadFile: (fileId: number, index: number) => void
  isDownloadings: boolean[]
}>()

const MAX_REGISTER_FILE_LENGTH = 5

const modelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emits('update:modelValue', value);
  },
})

const totalTime = computed(() => {
  const endTime = new Date(modelValue.value.end_time)
  const startTime = new Date(modelValue.value.start_time)
  const diffMiliSeconds = endTime.getTime() - startTime.getTime()

  const minuts = Math.floor(diffMiliSeconds / 1000 / 60) % 60
  const hours = Math.floor(diffMiliSeconds / 1000 / 60 / 60)
  return i18n.global.t('pages.dailyReports.unexpectedEventRegisterModal.times', { hours: hours, minuts: minuts})
})

const safeFiles = computed(() => {
  return Array.isArray(modelValue.value.files) ? modelValue.value.files : []
})

const inputFileLength = computed(() => {
  if(props.registeredFiles.length) {
    if(props.registeredFiles.length == MAX_REGISTER_FILE_LENGTH) return 0
  }
  return safeFiles.value.length
})

const isDisplayAddButton = computed(() => {
  const totalLenfth = props.registeredFiles.length + safeFiles.value.length
  return totalLenfth < MAX_REGISTER_FILE_LENGTH
})

function addColumn() {
  if (!Array.isArray(modelValue.value.files)) {
    modelValue.value.files = []
  }
  modelValue.value.files.push([])
}

function fileDelete(index: number) {
  if (!Array.isArray(modelValue.value.files)) {
    modelValue.value.files = []
    return
  }
  if (modelValue.value.files.length == 1) {
    return modelValue.value.files[0] = []
  }
  modelValue.value.files.splice(index, 1)
}
</script>
<template>
  <v-row class="mt-2">
    <v-col cols="4">
      <DatePicker
        v-model="modelValue.start_time"
        :label="i18n.global.t('pages.dailyReports.unexpectedEventRegisterModal.startTime')"
        :required="true"
        :errors="props.errors.start_time"
      />
    </v-col>
    <v-col cols="4">
      <DatePicker
        v-model="modelValue.end_time"
        :label="i18n.global.t('pages.dailyReports.unexpectedEventRegisterModal.endTime')"
        :required="true"
        :errors="props.errors.end_time"
      />
    </v-col>
    <v-col cols="4">
      <label>
        {{ i18n.global.t('pages.dailyReports.unexpectedEventRegisterModal.totalTime') }}
      </label>
      <v-text-field
        variant="underlined"
        v-model="totalTime"
        readonly
        density="compact"
        hide-details="auto"
        :clearable="false"
      />
    </v-col>
  </v-row>
  <v-row>
    <v-col class="py-1 mt-2">
      <label>
        {{ i18n.global.t('pages.dailyReports.unexpectedEventRegisterModal.address') }}
        <span style="color: red">✴︎</span>
      </label>
      <v-text-field
        v-model="modelValue.address"
        density="compact"
        hide-details="auto"
        maxlength="100"
        class="mt-2"
        :error-messages="props.errors.address"
      />
    </v-col>
  </v-row>
  <v-row>
    <v-col>
      <label>
        {{ i18n.global.t('pages.dailyReports.unexpectedEventRegisterModal.note') }}
        <span style="color: red">✴︎</span>
      </label>
      <v-textarea
        v-model="modelValue.note"
        density="compact"
        hide-details="auto"
        maxlength="300"
        class="mt-2"
        :error-messages="props.errors.note"
      />
    </v-col>
  </v-row>
  <v-row>
    <v-col>
      <label class="mb-2">
        {{ i18n.global.t('pages.dailyReports.unexpectedEventRegisterModal.documents') }}
      </label>
      <template v-for="(number, i) in inputFileLength" :key="number">
        <v-row>
          <v-col cols="10">
            <v-file-input
              v-model="modelValue.files[i]"
              :error-messages="props.errors[`files.${i}`]"
            />
          </v-col>
          <v-col cols="2" class="d-flex justify-end">
            <v-btn
              color="red"
              rounded="xl"
              variant="text"
              @click="fileDelete(i)"
            >
              <span class="material-symbols-outlined">
                delete
              </span>
            </v-btn>
          </v-col>
        </v-row>
      </template>
      <template v-for="(file, index) in props.registeredFiles" :key="index">
        <v-row v-if="file">
          <v-col cols="7">
            <RegisteredFile :file="file" />
          </v-col>
          <v-col cols="3" class="d-flex justify-end">
            <v-btn
              class="exec-btn"
              @click="props.downloadFile(file.id, index)"
              :loading="props.isDownloadings[index]"
            >
              {{ i18n.global.t('common.downloadBtn') }}
            </v-btn>
          </v-col>
          <v-col cols="2" class="d-flex justify-end">
            <v-btn
              color="red"
              rounded="xl"
              variant="text"
              @click="props.deleteRegisteredFile(index, file)"
            >
              <span class="material-symbols-outlined">
                delete
              </span>
            </v-btn>
          </v-col>
        </v-row>
      </template>
      <v-col
        v-if="isDisplayAddButton"
        cols="12" class="d-flex justify-end ml-4">
        <v-btn
          class="mr-2"
          color="primary"
          rounded="xl"
          variant="text"
          @click="addColumn"
        >
          <span class="material-symbols-outlined">
            add
          </span>
        </v-btn>
      </v-col>
    </v-col>
  </v-row>
</template>
